import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { PREMIUM_STORES_REQUEST } from 'app/store/ActionTypes';
import { premiumStoresRequest, resetRequestStatus } from 'app/store/Actions';
import { seoTitleAndMetaDescForStores } from 'app/utilities/Utils';
import { Helmet } from 'react-helmet';
import homeNewApis from 'api/home';
import { Col, Container, Row } from 'react-bootstrap';
import Shimmer from 'app/shimmer/Shimmer';
import { Placeholder } from 'app/components/common';
import { Constants } from 'app/utilities';
import OrderTracking from 'app/components/derived/orderTracking';
import FooterMobile from 'app/layout/footer/FooterMobile';
import Footer from 'app/layout/footer/Footer';
import ShopCard from 'app/components/common/ShopCard';
import { STORES } from 'app/events/screens';
import StoreCategory from './StoreCategory';
import StoreFilters from './StoreFilters';

const isTablet = window.screen.width >= 768 && window.screen.width < 992;
const isMobile = window.screen.width < 992;

function StoresV2(props) {
  const {
    pageType, match,
  } = props;
  const { urlCat } = match.params;
  const getStoreTypeId = () => {
    if (urlCat) {
      const urlArray = urlCat.split('-');
      return urlArray[urlArray.length - 1];
    }
    return '';
  };
  const storeTypeId = getStoreTypeId();
  const mainData = useSelector((state) => state.main);
  const dispatch = useDispatch();
  const {
    premiumStores, selectedAddress, requestsProcessing, requestsFailure,
    language,
  } = mainData;
  const quickFilters = premiumStores?.data?.filters || [];
  const storeLoading = requestsProcessing[PREMIUM_STORES_REQUEST];
  const storeFailure = requestsFailure[PREMIUM_STORES_REQUEST];
  const [filterId, setFilterId] = useState('');
  const [storeCategories, setStoreCategories] = useState({
    loading: false,
    failure: false,
    data: null,
  });

  const getStoresCategories = () => {
    setStoreCategories({
      ...storeCategories,
      loading: true,
      failure: false,
    });
    homeNewApis.homeNew(null, {
      latitude: selectedAddress ? selectedAddress.location.lat : '19.1207983',
      longitude: selectedAddress ? selectedAddress.location.lng : '72.8782323',
      isFreeSample: false,
      isUnifiedHome: true,
    }).then((res) => {
      setStoreCategories({
        ...storeCategories,
        loading: false,
        failure: false,
        data: res.data.data.storeCategories,
      });
    }).catch(() => {
      setStoreCategories({
        ...storeCategories,
        loading: false,
        failure: true,
      });
    });
  };

  const getStores = () => {
    dispatch(premiumStoresRequest(
      storeTypeId,
      filterId,
    ));
  };

  useEffect(() => {
    getStoresCategories();
  }, []);

  useEffect(() => {
    getStores();
    return () => {
      dispatch(
        resetRequestStatus([
          PREMIUM_STORES_REQUEST,
        ]),
      );
    };
  }, [filterId, storeTypeId]);

  const seoTitleAndDescription = seoTitleAndMetaDescForStores[pageType === 'ALL_SHOPS' ? 'ALL_SHOPS' : storeTypeId] || {
    title: 'GROCERIES',
    seoTitle: 'Grocery Shops Near Me | Buy Groceries from Local Stores on LoveLocal',
    seoMetaDescription: 'Online grocery shopping through grocery shops located near you. With various payment modes, exicting offers and home delivery option available, get your grocery delivered with ease. Try LoveLocal today.',
    canonicalUrl: 'https://www.lovelocal.in/shops/c/groceries-38',
    h1Tag: 'Browse Grocery Stores Near You',
  };

  const pageMeta = (
    <Helmet>
      <title>
        {seoTitleAndDescription.seoTitle}
      </title>
      <meta
        name="description"
        content={seoTitleAndDescription.seoMetaDescription}
      />
      <link
        rel="canonical"
        href={seoTitleAndDescription.canonicalUrl}
      />
    </Helmet>
  );
  return (
    <div
      id="stores"
      className="bg-white px-0 mx-0"
    >
      {pageMeta}
      {!isMobile && (
        <OrderTracking
          {...props}
          page="Shops"
        />
      )}
      {(() => {
        let shopsJSX = null;
        if (
          (
            !storeCategories.data
            && storeCategories.failure
          ) || (
            !premiumStores
            && storeFailure
          )
        ) {
          shopsJSX = (
            <Placeholder
              language={language}
              handleRetry={() => {
                getStoresCategories();
                getStores();
              }}
              imageSrc="/images/error-placeholder.png"
              heading={Constants.String.OH_NO[language]}
            />
          );
        } else {
          shopsJSX = (
            <Container>
              <Row>
                <Col
                  xs="auto"
                  md={isTablet ? 24 : 7}
                  className={`${isMobile || isTablet ? 'px-0 py-0 fixed-top w-100' : 'px-0 mx-0 py-2'}`}
                >
                  {
                    storeCategories.loading ? (
                      <Shimmer
                        shimmerType="SHOPS_CATEGORY_CARD"
                      />
                    ) : (
                      <StoreCategory
                        {...props}
                        category={storeCategories.data}
                        urlId={storeTypeId}
                      />
                    )
                  }
                </Col>
                <Col xs="auto" md={isTablet ? 24 : 17} className="py-sm-4 py-md-2 mx-0 px-0">
                  <Row className={`${isMobile || isTablet ? 'top-100' : ''} mx-0 px-1 py-2 pt-sm-6`}>
                    <StoreFilters
                      {...props}
                      filters={quickFilters}
                      onSelectFilter={(id) => {
                        setFilterId(id);
                      }}
                      selectedFilter={filterId}
                    />
                  </Row>
                  <Row className="px-xs-2 px-md-0 py-xs-4 py-0 mx-0">
                    {
                    !storeLoading && storeFailure && (
                      <Placeholder
                        language={language}
                        handleRetry={() => {
                          getStoresCategories();
                          getStores();
                        }}
                        imageSrc="/images/error-placeholder.png"
                        heading={Constants.String.OH_NO[language]}
                      />
                    )
                  }
                    {
                    (storeLoading) && (
                      <Row
                        className="w-100 py-3 mx-0 mt-3 justify-content-center"
                      >
                        <Shimmer
                          shimmerType="STORE_CARDS_LAYOUT"
                        />
                      </Row>
                    )
                  }
                    {
                    !storeLoading
                    && !storeFailure
                    && !!premiumStores
                    && (
                      (premiumStores?.data?.stores).length === 0 ? (
                        <div>
                          <Placeholder
                            language={language}
                            imageSrc="/images/no-results.png"
                            heading={Constants.String.NO_RESULTS_FOUND[language]}
                          />
                        </div>
                      ) : '')
                  }
                    {
                  !storeLoading
                  && !storeFailure
                  && premiumStores
                  && premiumStores.count >= 1
                  && (
                  <>
                    <Col
                      xs={24}
                      className="pt-3"
                    >
                      <Row>
                        <h1
                          className="pl-3 fs-3"
                        >
                          {seoTitleAndDescription.h1Tag}
                        </h1>
                      </Row>
                    </Col>
                    <Col
                      xs={24}
                    >
                      <Row>
                        {
                          (premiumStores?.data?.stores || []).map((item) => (
                            <ShopCard
                              key={item.storeId}
                              {...props}
                              item={item}
                              screen={STORES}
                              isMobile={isMobile}
                            />
                          ))
                        }
                      </Row>
                    </Col>
                  </>
                  )
                }
                  </Row>
                </Col>
              </Row>
            </Container>
          );
        }
        return shopsJSX;
      })()}
      <div
        className="d-md-none bg-black"
      >
        <FooterMobile
          {...props}
          language={language}
        />
        <div className="p-6" />
      </div>
      <div
        className="d-none d-md-block bg-black"
      >
        <Footer
          {...props}
          language={language}
        />
      </div>
    </div>
  );
}

StoresV2.propTypes = {
  pageType: PropTypes.string.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      urlCat: PropTypes.string,
    }),
  }).isRequired,
};

export default StoresV2;
